/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { useGlobal } from '@rcg/ui-react';
import clsx from 'clsx';
import { formatPrice } from '@rcg/pricing-format';
import { initScriptTag } from '@rcg/unified-payment-component';
import { navigate } from '@gatsbyjs/reach-router';
import { globalizationPath } from '../../utils/globalization/globalizationPath';
import useDictionary from '../../hooks/useDictionary';
import creditCardStyles from './creditCardStyles';
import useStyles from '../CheckoutCard/useStyles';
import setBookedReservation from '../../operations/localState/setBookedReservation';
import setPayment from '../../operations/localState/setPayment';
import Dictionary from '../../utils/dictionary';
import {
  BOOKING_COUNTRY,
  NAVIGATION_PATHS,
  PAY_NOW,
  BOOKING_STATUS,
  SESSION_STORAGE_BOOKING_PROFILE_NAME,
  SESSION_STORAGE_CHANGE_FLIGHTS,
  SESSION_STORAGE_GDS_LOCATOR,
  GROUP_BOOKING,
} from '../../utils/constants';
import { getSessionStorageItem } from '../../operations/sessionStorage/utils';
import getBrand from '../../utils/getBrand';
import { snakeToCamel } from '../../utils/common';
import NotificationBannerComponent from '../NotificationBanner';
import { CreditCardInformationForm } from '../CheckoutCard';
import InfoTooltip from '../InfoTooltip';
import PaymentOption from '../PaymentOption';
import useBookFlightAuthorization from '../../operations/dataAccess/useBookFlightAuthorization';
import useGuestAirProfile from '../../operations/dataAccess/useGuestAirProfile';
import useCommonStyles from '../../utils/useCommonStyles';
import { getTrackPaymentData } from '../../utils/analytics/searchTracking';

const enableCTA = (paymentSelection, noErrors) => {
  if (paymentSelection === 'reserve') {
    return true;
  }
  if (paymentSelection === 'payNow') {
    return noErrors;
  }
  return false;
};
const currentBrand = getBrand();

const CreditCardInformation = ({
  holdFlightData,
  extraData,
  bookingProfile,
  showCancelPaywayBanner,
  showErrorvalidationBanner,
  payment3dsToggle,
  paymentSelection,
  handleOnChangeCTA,
  setCreditCard,
  disableForm,
  giftCards,
  bookFlightPayment,
  setSubmitPaymentRef,
  heldConfirmInputInformation,
  royalPayway,
  onPropertyChage,
  isRefund,
  totalBalance,
  paymentMethodFormRef,
  showLineError,
  unifiedPaymentEnabled,
  setPayLaterFromAmend,
  showPaymentErrorBanner,
  onFinishUpcPayment,
  loading,
}) => {
  const classes = useStyles();
  const {
    data: { localizedDayjs, lang, market },
  } = useGlobal();
  const getDictionary = useDictionary(Dictionary);
  const classesCreditCard = creditCardStyles();
  const styles = useCommonStyles();
  const [errorBannerTitle, setErrorBannerTitle] = useState('');
  const [errorBannerMessage, setErrorBannerMessage] = useState('');
  const [errorBannerType, setErrorBannerType] = useState('');
  const [showNotification, setNotification] = useState(true);
  const [errorBanner, setErrorBanner] = useState(false);
  const [_, setIsLoadingPayment] = useState(false);
  const [noErrors, setNoErrors] = useState(false);
  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const [creditCardFormRender, setCreditCardFormRender] = useState(true);

  const { returnFlight, departureFlight } = extraData;
  const { priceChange } = holdFlightData;
  const handleErrorBanner = ({ title, message, type = 'error' }) => {
    setErrorBannerTitle(title);
    setErrorBannerType(type);
    setErrorBannerMessage(message);
    setErrorBanner(true);
    document?.querySelector('.main-container')?.scrollTo(0, 0);
  };

  const [guestAirProfile, { data: guestAirProfileData, error: guestAirProfileError }] =
    useGuestAirProfile({ fetchPolicy: 'network-only' });

  const [bookFlightUpc] = useBookFlightAuthorization({
    onCompleted: (data) => {
      const { bookFlightAuthorization } = data;
      setBookedReservation({ bookFlight: bookFlightAuthorization });
      setPayment({
        method: PAY_NOW,
      });
      onFinishUpcPayment();
      navigate(`${urlPath}${NAVIGATION_PATHS.confirmation}`);
    },
    onError: () => {
      const bookingInfo = sessionStorage.getItem(SESSION_STORAGE_BOOKING_PROFILE_NAME);
      const bookingParse = JSON.parse(bookingInfo);
      const guestIds = heldConfirmInputInformation?.guests?.map((guest) => guest.id);
      const bookingId = bookingParse?.currentBooking?.id;
      const locators = sessionStorage.getItem(SESSION_STORAGE_GDS_LOCATOR);
      const changeFlightsStatus = sessionStorage.getItem(SESSION_STORAGE_CHANGE_FLIGHTS);
      setTimeout(
        () =>
          guestAirProfile({
            variables: {
              input: {
                bookingId,
                guestIds,
                correlationID: heldConfirmInputInformation?.correlationID,
                locators: changeFlightsStatus === 'true' ? locators : [],
              },
            },
          }),
        5000,
      );
    },
  });
  const handleFormChange = ({ card, errors, values }) => {
    setCreditCard(values);
    if (!card.error && Object.keys(errors).length === 0) {
      setNoErrors(true);
    } else {
      setNoErrors(false);
    }
  };

  const handleSubmitPaymentRef = (submitRefVar) => {
    setSubmitPaymentRef(submitRefVar);
  };

  const amountValue = priceChange
    ? priceChange.newTotal.value
    : returnFlight.cabin.totalPrice.value || departureFlight.cabin.totalPrice.value;

  const {
    cabin: { isPaymentRequired, isRefundable },
  } = returnFlight || departureFlight;

  const {
    flightProfile: {
      id: bookingId,
      currency: currencyCode,
      booking: { finalPaymentDate: balanceDueDate },
    },
  } = bookingProfile;

  const { country, officeID } = getSessionStorageItem(BOOKING_COUNTRY) || {
    country: 'USA',
    officeID: 'MIA',
  };

  const checkPaymentDetail = (detail) => {
    if (detail?.staterooms?.[0]?.payments?.[0]?.paymentOption === 'courtesy_hold') {
      setPayLaterFromAmend(true);
    } else {
      setPayLaterFromAmend(false);
    }
  }; // map info for footer tooltip

  const extractNumericGroupId = (rawId) => {
    if (!rawId) return undefined;
    if (!Number.isNaN(Number(rawId)) && String(rawId).trim() !== '') return Number(rawId);
    const parts = String(rawId).split('-');
    if (parts.length === 2 && !Number.isNaN(Number(parts[1])) && parts[1].trim() !== '') {
      return Number(parts[1]);
    }
    return undefined;
  };

  const checkPaymentResponse = (paymentResponse) => {
    if (paymentResponse?.payments[0]?.status === 'SUCCESS') {
      const amount = paymentResponse.payments[0]?.amount || 0;
      const airGuests = heldConfirmInputInformation?.guests || [];
      const passengers = airGuests.length || 1;
      const amountPerPassanger = amount / passengers;
      const isGrpBooking = bookingProfile?.bookingType === GROUP_BOOKING;
      const rawGroupId = isGrpBooking
        ? bookingProfile?.currentBooking?.sailing?.groupId ||
          getSessionStorageItem(SESSION_STORAGE_BOOKING_PROFILE_NAME)?.currentBooking?.sailing
            ?.groupId
        : undefined;
      const groupID = extractNumericGroupId(rawGroupId);
      const authorizationInfo = airGuests.map((guest) => {
        return {
          amount: amountPerPassanger,
          authorizationCode: paymentResponse?.payments[0]?.controlSequenceNumber,
          bookingID: paymentResponse?.payments[0]?.bookingId[0],
          controlSequenceNumber: paymentResponse?.payments[0]?.controlSequenceNumber,
          guestID: guest.id,
          ...(groupID !== undefined && { groupID }),
        };
      });
      const heldConfirmInput = heldConfirmInputInformation?.guests?.[0]?.emailAddress
        ? heldConfirmInputInformation
        : getSessionStorageItem('emailInfo');
      bookFlightUpc({
        variables: {
          heldConfirmInput,
          authorizationInfo,
        },
      });
    } else {
      onFinishUpcPayment();
    }
  };
  const checkLoading = (isLoading) => {
    setIsLoadingPayment(isLoading);
  };
  const checkUTag = () => {}; // utag info if is required

  const checkIsDisableCTA = (isDisableCta) => {
    if (unifiedPaymentEnabled) {
      handleOnChangeCTA(!isDisableCta);
    }
  };

  const UPCSetup = () => {
    const countryCode = country;
    const depositAmount = totalBalance;
    const parsedBooking = getSessionStorageItem(SESSION_STORAGE_BOOKING_PROFILE_NAME, true);

    const depositAmountFormatValue = formatPrice(depositAmount, currencyCode, true, true, true);
    const fullPaymentDueToday = parsedBooking?.currentBooking?.price?.inFinal;
    const dueDate = localizedDayjs(balanceDueDate).format('MMM D[,] YYYY');

    let requiredPayDescriptionText = '';
    if (!fullPaymentDueToday && isRefundable) {
      requiredPayDescriptionText = `${getDictionary(
        Dictionary.airCheckoutPayNowInfo.key,
      )} ${dueDate}`;
    }
    if (fullPaymentDueToday) {
      requiredPayDescriptionText = getDictionary(Dictionary.airFullPaymentDueTodayLabel.key);
    }

    const paymentTypes = [
      {
        description: requiredPayDescriptionText,
        boldDescription: '',
        amount: totalBalance,
        title: depositAmountFormatValue,
        type: 'remaining_balance',
      },
    ];

    if (!isPaymentRequired && !fullPaymentDueToday) {
      paymentTypes.push({
        description: `${getDictionary(Dictionary.airCheckoutNoPaymentTodayInfo.key)} ${dueDate}`,
        boldDescription: '',
        amount: totalBalance,
        title: getDictionary(Dictionary.airCheckoutNoPaymentTodayLabel.key),
        type: 'courtesy_hold',
      });
    }
    const {
      setup,
      listeners: { paymentDetail, disableCTA, getPaymentResponse, isLoading, uTag },
    } = window.UnifiedPaymentComponent;
    const trimBookingId = String(parseInt(bookingId, 10));

    const onlyPaymentOption = isPaymentRequired || fullPaymentDueToday;
    const pageView = {
      pageName: 'airbooking:payment',
      pageType: 'flightPayment',
      bookingId: trimBookingId,
      ...getTrackPaymentData(onlyPaymentOption),
      paymentTypeAvailable: paymentTypes.map((e) => snakeToCamel(e.type)).join(','),
    };

    const analytics = {
      uTag: {
        pageView,
      },
    };

    const setupProps = {
      bookingId: trimBookingId,
      bookingInfo: {
        orderId: bookingId,
        countryCode,
        packageCode: parsedBooking?.currentBooking?.sailing?.itinerary?.code,
        currencyCode,
        officeCode: officeID.toUpperCase(),
        balanceDueDate,
        depositAmount,
        status: BOOKING_STATUS.booked,
        passengerId: heldConfirmInputInformation?.guests?.[0]?.id,
      },
      service: {
        env: process.env.REACT_APP_UNIFIED_PAYMENT_ENV,
        brand: currentBrand.name,
        channel: 'ROYAL_WEB_AIR',
        type: 'CRUISE_BOOKING',
        client: 'AIR',
        language: lang,
      },
      theme: 'primary',
      custom: {},
      useUPCLoader: false,
      paymentTypes,
      analytics,
    };
    setup(setupProps);
    paymentDetail(checkPaymentDetail);
    getPaymentResponse(checkPaymentResponse);
    disableCTA(checkIsDisableCTA);
    isLoading(checkLoading);
    uTag(checkUTag);
  };

  useEffect(() => {
    initScriptTag({
      brand: currentBrand.name,
      env: process.env.REACT_APP_UNIFIED_PAYMENT_VERSION,
    });
    document.addEventListener('UPC_setup', UPCSetup);
    return () => {
      document.removeEventListener('UPC_setup', UPCSetup);
    };
  }, []);

  useEffect(() => {
    if (guestAirProfileData?.guestAirProfile?.hasFlights) {
      const { guestAirProfile: dataProfile } = guestAirProfileData;
      setBookedReservation({ bookFlight: dataProfile });
      setPayment({
        method: PAY_NOW,
      });
      navigate(`${urlPath}${NAVIGATION_PATHS.confirmation}`);
    } else if (guestAirProfileData?.guestAirProfile?.hasFlights === false || guestAirProfileError) {
      setErrorBanner(true);
      showPaymentErrorBanner(
        getDictionary(Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesTitle.key),
        getDictionary(
          Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesDescription.key,
        ),
      );
    }
    onFinishUpcPayment();
  }, [guestAirProfileData, guestAirProfileError]);

  useEffect(() => {
    // Legacy Payment UseEffect
    if (!unifiedPaymentEnabled) {
      const isFormEnabled = enableCTA(paymentSelection, noErrors);
      handleOnChangeCTA(isFormEnabled);
    }
  }, [noErrors]);

  return (
    <div className={`${classes.cardContainerCheckoutWrapper} ${classesCreditCard.elevation}`}>
      {errorBanner && showNotification && (
        <div className={`${styles.notification} ${classes.bannerNotification}`}>
          <NotificationBannerComponent
            title={errorBannerTitle}
            description={errorBannerMessage}
            type={errorBannerType}
            renderCloseBtn
            isBannerOpen
            onClose={() => {
              setErrorBanner(false);
              setNotification(!showNotification);
            }}
          />
        </div>
      )}
      <div
        className={clsx(
          classes.cardContainerCheckoutPayment,
          classesCreditCard.cardContainerCheckoutPayment,
        )}>
        <div className={classesCreditCard.creditCardTitle}>
          {getDictionary(Dictionary.airPaymentMethodLabel.key)}
          <InfoTooltip
            description={getDictionary(Dictionary.airPaymentReviewAndPaySubTitleLabel.key)}
          />
        </div>
        {unifiedPaymentEnabled ? (
          <react-unified-payment />
        ) : (
          <>
            <PaymentOption
              holdFlightData={holdFlightData}
              extraData={extraData}
              bookingProfile={bookingProfile}
              onPropertyChage={onPropertyChage}
              isRefund={isRefund}
              totalBalance={totalBalance}
              setCreditCardFormRender={setCreditCardFormRender}
              loading={loading}
            />
            <div
              className={clsx(
                classes.creditCardInformation,
                paymentSelection === 'payNow'
                  ? classes.creditCardInformationOpen
                  : classes.creditCardInformationClose,
              )}>
              {disableForm && (
                <span className={classesCreditCard.totalPaymentsHaveBeenMet}>
                  {getDictionary(Dictionary.airPaymentTotalPaymentsHaveBeenMet.key)}
                </span>
              )}
              {!disableForm && creditCardFormRender && (
                <div className={classesCreditCard.creditCardForm}>
                  <CreditCardInformationForm
                    onFormChange={handleFormChange}
                    paymentSelection={paymentSelection}
                    handleSubmitForm={(submitRefVar) => {
                      handleSubmitPaymentRef(submitRefVar);
                    }}
                    amountToPay={amountValue}
                    heldConfirmInput={heldConfirmInputInformation}
                    bookFlight={bookFlightPayment}
                    bookingProfile={bookingProfile}
                    setIsLoading={setIsLoadingPayment}
                    showCancelPaywayBanner={showCancelPaywayBanner}
                    showErrorvalidationBanner={showErrorvalidationBanner}
                    payment3dsToggle={payment3dsToggle}
                    disableForm={disableForm}
                    giftCards={giftCards}
                    royalPayway={royalPayway}
                    paymentMethodFormRef={paymentMethodFormRef}
                    showLineError={showLineError}
                    handleErrorBanner={handleErrorBanner}
                    onFinishCreditCardFormSubmit={() => onFinishUpcPayment()}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

CreditCardInformation.propTypes = {
  heldConfirmInputInformation: PropTypes.shape({
    guests: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        emailAddress: PropTypes.string,
      }),
    ),
  }),
  bookingProfile: PropTypes.shape({
    currentBooking: PropTypes.shape({
      sailing: PropTypes.shape({
        groupId: PropTypes.string,
      }),
    }),
  }),
};

export default CreditCardInformation;
